// Iframe install guide — 4-step walkthrough with copy-paste code snippets
// for the 4 most common stacks: Node/Express, WordPress (PHP), React, HTML.
//
// Personalised snippets: if the user pastes their EtOH API key in the
// "Personnaliser" input, the snippets fill in their key on the fly. The key
// is masked in the preview (last 4 chars only) but copied in full to the
// clipboard — it never leaves the browser unless the user explicitly copies.
import { useMemo, useState } from "react";
import { Check, Copy, KeyRound, ServerCog, Code2, Database, Rocket, Eye, EyeOff, Sparkles } from "lucide-react";
import { useT } from "@/lib/i18n";
import { renderInline } from "@/lib/renderInline";

// Mask all but the last 4 characters: "362e****0010"
const maskKey = (k) => {
  if (!k) return "";
  if (k.length <= 4) return "•".repeat(k.length);
  return k.slice(0, 4) + "•".repeat(Math.max(4, k.length - 8)) + k.slice(-4);
};

const PLACEHOLDER = "VOTRE_CLE_API_ETOH";

// Build a snippet by substituting the placeholder. `display` controls whether
// we render the masked or full key (we copy the FULL version to the clipboard
// so the user gets a paste-ready snippet).
const renderSnippet = (template, key, display = "full") => {
  if (!key) return template;
  const value = display === "masked" ? maskKey(key) : key;
  return template.split(PLACEHOLDER).join(value);
};

// Snippets are deliberately monolingual (English code comments) — translating
// inline code comments would just confuse copy-pasters. The descriptive UI
// around them is fully i18n'd.
const TEMPLATES = {
  node: `// /api/geovinum-token (Node / Express)
import express from "express";
const app = express();

let cache = { token: null, expiresAt: 0 };

app.get("/api/geovinum-token", async (req, res) => {
  if (cache.token && Date.now() < cache.expiresAt) {
    return res.json({ token: cache.token });
  }
  const r = await fetch("https://api.etoh.app/authentication/apikey/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ apikey: "${PLACEHOLDER}" }),
  });
  if (!r.ok) return res.status(503).json({ error: "geoVINUM unavailable" });
  const data = await r.json();
  const token = data.access;                 // EtOH returns { access: "<JWT>" }
  cache = { token, expiresAt: Date.now() + 55 * 60 * 1000 }; // 55 min
  res.json({ token });
});`,

  wordpress: `<?php
// functions.php — REST endpoint /wp-json/geovinum/v1/token
add_action('rest_api_init', function () {
  register_rest_route('geovinum/v1', '/token', [
    'methods' => 'GET',
    'permission_callback' => '__return_true',
    'callback' => function () {
      $cache = get_transient('geovinum_token');
      if ($cache) return ['token' => $cache];

      $res = wp_remote_post('https://api.etoh.app/authentication/apikey/', [
        'headers' => ['Content-Type' => 'application/json'],
        'body'    => wp_json_encode(['apikey' => '${PLACEHOLDER}']),
        'timeout' => 10,
      ]);
      if (is_wp_error($res) || wp_remote_retrieve_response_code($res) >= 400) {
        return new WP_Error('etoh_down', 'geoVINUM unavailable', ['status' => 503]);
      }
      $body  = json_decode(wp_remote_retrieve_body($res), true);
      $token = $body['access'] ?? null;       // EtOH returns { "access": "<JWT>" }
      if (!$token) return new WP_Error('etoh_empty', 'No token', ['status' => 503]);

      set_transient('geovinum_token', $token, 55 * MINUTE_IN_SECONDS);
      return ['token' => $token];
    },
  ]);
});`,

  react: `// React component — drop it anywhere
import { useEffect, useState } from "react";

export function GeoVinumMap({ appellation }) {
  const [token, setToken] = useState(null);

  useEffect(() => {
    fetch("/api/geovinum-token")
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => setToken(d.token))
      .catch(() => {}); // silent fail — article still renders
  }, []);

  if (!token || !appellation) return null;
  const src = \`https://iframe.geovinum.app/?token=\${encodeURIComponent(token)}&app=\${encodeURIComponent(appellation)}\`;
  return (
    <iframe
      src={src}
      title={\`Carte geoVINUM - \${appellation}\`}
      loading="lazy"
      style={{ width: "100%", height: 650, border: 0, borderRadius: 12 }}
    />
  );
}`,

  html: `<!-- Direct embed (vanilla JS, faithful to the official EtOH guide) -->
<div id="container-iframe"><div class="loadercss"></div></div>

<script>
  var appellation = "Sancerre";
  // Point this at YOUR server route (token_request.php / Node / etc.)
  fetch('/api/geovinum-token')
    .then(function(r) { return r.json(); })
    .then(function(d) {
      // EtOH returns { access: "<JWT>" } ; some proxies return { token: "..." }
      var token = d.access || d.token;
      if (!token) return;
      var src = 'https://iframe.geovinum.app/?token=' + encodeURIComponent(token)
              + '&app=' + encodeURIComponent(appellation);
      document.getElementById('container-iframe').innerHTML =
        '<iframe src="' + src + '" title="Carte geoVINUM - ' + appellation + '"'
        + ' style="width:100%;height:650px;border:0;border-radius:12px"></iframe>';
    });
</script>`,
};

const TABS = [
  { id: "node",      label: "Node / Express", icon: ServerCog },
  { id: "wordpress", label: "WordPress",      icon: ServerCog },
  { id: "react",     label: "React",          icon: Code2 },
  { id: "html",      label: "HTML",           icon: Code2 },
];

const STEP_DEFS = [
  { key: "step1", icon: KeyRound },
  { key: "step2", icon: ServerCog },
  { key: "step3", icon: Database },
  { key: "step4", icon: Rocket },
];

const CopyButton = ({ text, testId, copyLabel, copiedLabel }) => {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard API blocked (insecure context) — silent fail
    }
  };
  return (
    <button
      onClick={onClick}
      className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-[11.5px] transition-colors backdrop-blur-sm"
      data-testid={testId}
      aria-label={copyLabel}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? copiedLabel : copyLabel}
    </button>
  );
};

const IframeInstall = () => {
  const t = useT();
  const [tab, setTab] = useState("node");
  const [userKey, setUserKey] = useState("");
  const [showKey, setShowKey] = useState(false);

  // What appears on screen: masked when the user hides it, raw when they
  // explicitly toggle "show". Either way the clipboard always gets the full
  // key — copying a half-masked snippet would be useless.
  const display = useMemo(() => renderSnippet(TEMPLATES[tab], userKey, showKey ? "full" : "masked"), [tab, userKey, showKey]);
  const fullCopy = useMemo(() => renderSnippet(TEMPLATES[tab], userKey, "full"), [tab, userKey]);
  const hasKey = userKey.trim().length > 0;

  return (
    <section className="py-16 md:py-24 bg-white border-t border-[color:var(--gv-line)]" data-testid="iframe-install">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] text-[color:var(--gv-blue)] px-3 py-1.5 rounded-[5px] text-[10.5px] uppercase tracking-[0.15em] font-medium mb-4 shadow-sm">
            <Rocket size={11} /> {t("iframeInstall.eyebrow")}
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] leading-[1.1] mb-3">
            {t("iframeInstall.title")}
          </h2>
          <p className="text-[14.5px] md:text-[16px] text-[color:var(--gv-ink)]/65 max-w-2xl mx-auto">
            {t("iframeInstall.subtitle")}
          </p>
        </div>

        {/* 4-step grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          {STEP_DEFS.map(({ key, icon: Icon }) => (
            <div key={key} className="bg-[color:var(--gv-mist)]/40 border border-[color:var(--gv-line)] rounded-[10px] p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-[8px] bg-white border border-[color:var(--gv-line)] flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-[color:var(--gv-pink)]" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-[16px] text-[color:var(--gv-ink)] mb-1.5">{t(`iframeInstall.${key}.title`)}</div>
                  <p className="text-[13.5px] text-[color:var(--gv-ink)]/70 leading-relaxed">{renderInline(t(`iframeInstall.${key}.body`))}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Personalised snippet — paste-your-key */}
        <div className="bg-gradient-to-br from-[color:var(--gv-pink)]/5 to-[color:var(--gv-blue)]/5 border border-[color:var(--gv-pink)]/20 rounded-[10px] p-5 mb-6" data-testid="iframe-install-personalize">
          <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
            <div className="w-10 h-10 rounded-[8px] bg-white border border-[color:var(--gv-line)] flex items-center justify-center shrink-0">
              <Sparkles size={18} className="text-[color:var(--gv-pink)]" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="font-display text-[16px] text-[color:var(--gv-ink)] mb-1">{t("iframeInstall.personalize.title")}</div>
              <p className="text-[12.5px] text-[color:var(--gv-ink)]/65 mb-3">
                {renderInline(t("iframeInstall.personalize.body"))}
              </p>
              <div className="flex gap-2 items-stretch">
                <div className="relative flex-1">
                  <input
                    type={showKey ? "text" : "password"}
                    value={userKey}
                    onChange={(e) => setUserKey(e.target.value)}
                    placeholder={t("iframeInstall.personalize.placeholder")}
                    className="w-full h-10 px-3 pr-10 rounded-[5px] border border-[color:var(--gv-line)] bg-white text-[13.5px] font-mono focus:outline-none focus:border-[color:var(--gv-pink)] transition-colors"
                    data-testid="iframe-install-key-input"
                    autoComplete="off"
                    spellCheck="false"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40 hover:text-[color:var(--gv-ink)]/80"
                    aria-label={showKey ? t("iframeInstall.hide_key") : t("iframeInstall.show_key")}
                    data-testid="iframe-install-key-toggle"
                  >
                    {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {hasKey && (
                  <button
                    onClick={() => setUserKey("")}
                    className="px-3 h-10 text-[12.5px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-ink)] border border-[color:var(--gv-line)] rounded-[5px] bg-white"
                    data-testid="iframe-install-key-clear"
                  >
                    {t("iframeInstall.personalize.clear")}
                  </button>
                )}
              </div>
              {hasKey && (
                <div className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-[5px]" data-testid="iframe-install-key-ready">
                  <Check size={12} />
                  {renderInline(t("iframeInstall.personalize.ready"), [maskKey(userKey)])}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Code snippets tabs */}
        <div data-testid="iframe-install-snippets">
          <div className="flex items-center gap-1 border-b border-[color:var(--gv-line)] mb-0 overflow-x-auto">
            {TABS.map((tb) => {
              const Icon = tb.icon;
              const active = tab === tb.id;
              return (
                <button
                  key={tb.id}
                  onClick={() => setTab(tb.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] border-b-2 -mb-px transition-colors whitespace-nowrap ${
                    active
                      ? "border-[color:var(--gv-pink)] text-[color:var(--gv-ink)]"
                      : "border-transparent text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]/85"
                  }`}
                  data-testid={`iframe-install-tab-${tb.id}`}
                >
                  <Icon size={13} /> {tb.label}
                </button>
              );
            })}
          </div>

          <div className="relative bg-[color:var(--gv-ink)] rounded-b-[10px] overflow-hidden">
            <CopyButton
              text={fullCopy}
              testId={`iframe-install-copy-${tab}`}
              copyLabel={t("iframeInstall.copy")}
              copiedLabel={t("iframeInstall.copied")}
            />
            <pre className="p-5 md:p-6 text-[12.5px] leading-[1.6] text-white/85 overflow-x-auto font-mono" data-testid={`iframe-install-snippet-${tab}`}>
              <code>{display}</code>
            </pre>
          </div>
        </div>

        <p className="text-center text-[12px] text-[color:var(--gv-ink)]/50 mt-6">
          {t("iframeInstall.help_prefix")}{" "}
          <a href="/contact" className="text-[color:var(--gv-pink)] hover:underline">{t("iframeInstall.help_link")}</a>{" "}
          {t("iframeInstall.help_suffix")}
        </p>
      </div>
    </section>
  );
};

export default IframeInstall;
